import { Clock, Eye, Merge, MoreVertical, Plus, Printer, Receipt, RotateCcw, User, XCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { reais, tempoAberta } from "./lib";

const nn = (c) => String(c?.numero || 0).padStart(2, "0");

/** Os ⋮ do cartão: 2ª vias, juntar, reabrir e cancelar (cancelar fica fora da comanda; juntar
 * fica nos dois lugares — dono, 25/10/2026). */
function MenuComanda({ c, emFechamento, podeJuntar, onImprimir, onJuntar, onReabrir, onCancelar }) {
  const temItens = !!c.itens;
  const item = (acao, Icone, texto, extra = {}) => (
    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); acao(); }} {...extra}>
      <Icone className="w-4 h-4 mr-2" /> {texto}
    </DropdownMenuItem>
  );
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()} data-e2e="comanda-menu" title="Mais opções" aria-label={`Mais opções da comanda ${nn(c)}`}
          className="p-1.5 -mr-1.5 rounded-md hover:bg-muted transition-colors touch-manipulation">
          <MoreVertical className="w-4 h-4 text-muted-foreground hover:text-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        {emFechamento && item(() => onReabrir(c), RotateCcw, "Reabrir comanda", { "data-e2e": "comanda-reabrir" })}
        {temItens && (
          <>
            {!emFechamento && item(() => onImprimir(c, "ambos"), Printer, "2ª via de ambos")}
            {item(() => onImprimir(c, "fechamento"), Printer, emFechamento ? "Reimprimir fechamento" : "2ª via Fechamento", { "data-e2e": "comanda-2via-fechamento" })}
            {item(() => onImprimir(c, "preparo"), Printer, "2ª via Preparo", { "data-e2e": "comanda-2via-preparo" })}
          </>
        )}
        {/* Em fechamento não recebe item: juntar nela só depois de reabrir */}
        {podeJuntar && !emFechamento && (
          <>
            {temItens && <DropdownMenuSeparator />}
            {item(() => onJuntar(c), Merge, "Juntar comandas", { "data-e2e": "comanda-juntar-menu" })}
          </>
        )}
        {(temItens || emFechamento || podeJuntar) && <DropdownMenuSeparator />}
        {item(() => onCancelar(c), XCircle, "Cancelar comanda", {
          className: "text-red-600 focus:text-red-600 dark:text-red-400", "data-e2e": "comanda-cancelar-abrir",
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Cartão da comanda na lista, no molde do cartão da mesa (pedido do dono,
 * 25/10/2026): ⋮ com 2ª vias e cancelar, olhinho para ver a comanda,
 * ADICIONAR ITENS, Imprimir (cupom de fechamento + em fechamento) e Fechar
 * comanda (em fechamento sem cupom). Em fechamento o cartão fica âmbar,
 * "Aguardando pagamento", e o botão leva ao pagamento.
 */
export default function CartaoComanda({ c, podeJuntar, onAbrir, onVer, onCancelar, onImprimir, onJuntar, onEmFechamento, onReabrir, onPagar }) {
  const emFechamento = !!c.em_fechamento;
  const temItens = !!c.itens;
  const menu = <MenuComanda c={c} emFechamento={emFechamento} podeJuntar={podeJuntar} onImprimir={onImprimir} onJuntar={onJuntar}
    onReabrir={onReabrir} onCancelar={onCancelar} />;
  const olho = temItens && (
    <Button type="button" variant="outline" onClick={(e) => { e.stopPropagation(); onVer(c); }}
      className="shrink-0 h-auto px-2.5 py-2.5 touch-manipulation" title="Ver a comanda" aria-label="Ver a comanda" data-e2e="comanda-ver">
      <Eye className="w-4 h-4" />
    </Button>
  );

  return (
    <div data-e2e="comanda-cartao" data-status={emFechamento ? "fechamento" : "aberta"}
      className={`relative rounded-xl border overflow-hidden transition-all duration-200 bg-card ${emFechamento
        ? "border-amber-500 shadow-lg shadow-amber-500/20 bg-amber-50/50 dark:bg-amber-950/30 ring-2 ring-amber-500/30"
        : temItens
          ? "border-green-500/70 shadow-md shadow-green-500/15 ring-1 ring-green-500/25 hover:shadow-lg hover:shadow-green-500/20"
          : "border-dashed border-border hover:border-primary/50"}`}>
      <div className="p-3 sm:p-4">
        <div className="flex justify-between items-center gap-1 mb-2">
          <span className="font-mono text-[10px] sm:text-[11px] text-muted-foreground truncate">{c.pedido_codigo || "—"}</span>
          <div className="flex items-center gap-1 shrink-0">
            <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground" title="Tempo aberta">
              <Clock className="w-3 h-3" /> {tempoAberta(c.opened_at)}
            </span>
            {menu}
          </div>
        </div>

        {emFechamento && (
          <div className="text-center mb-2">
            <span className="inline-block px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 animate-pulse">
              Aguardando pagamento
            </span>
          </div>
        )}

        <div className="text-center mb-2">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Comanda</p>
          <div className="text-4xl sm:text-5xl font-black leading-none tabular-nums">{nn(c)}</div>
        </div>

        <div className="flex items-center justify-center gap-1 mb-2 text-[11px] text-muted-foreground min-w-0">
          <User className="w-3 h-3 shrink-0" />
          {c.cliente_nome ? <span className="truncate font-medium text-foreground">{c.cliente_nome}</span> : <span>Sem nome</span>}
        </div>

        <div className="text-center mb-3">
          <div className="text-lg sm:text-xl font-black text-orange-600 dark:text-orange-400 leading-tight tabular-nums whitespace-nowrap">{reais(c.total)}</div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {temItens ? `${c.itens} ${c.itens === 1 ? "item" : "itens"}` : "Sem itens"}
          </div>
        </div>

        {emFechamento ? (
          <div className="flex gap-1.5">
            {olho}
            <Button onClick={(e) => { e.stopPropagation(); onPagar(c); }} data-e2e="comanda-pagar"
              className={`flex-1 min-w-0 h-auto py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-bold touch-manipulation whitespace-nowrap ${olho ? "text-xs px-2" : "text-xs sm:text-sm"}`}>
              {!olho && <Receipt className="w-3.5 h-3.5 mr-1.5 shrink-0" />} FECHAR COMANDA
            </Button>
          </div>
        ) : (
          <>
            <div className="flex gap-1.5">
              {olho}
              <Button onClick={(e) => { e.stopPropagation(); onAbrir(c); }} data-e2e="comanda-adicionar"
                className={`flex-1 min-w-0 h-auto py-2.5 bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white font-bold touch-manipulation whitespace-nowrap leading-tight shadow-sm ${olho ? "text-xs px-2" : "text-xs sm:text-sm"}`}>
                {!olho && <Plus className="w-4 h-4 mr-1 shrink-0" />} ADICIONAR ITENS
              </Button>
            </div>
            {temItens && (
              <div className="w-full mt-1.5 grid grid-cols-2 gap-1">
                <button type="button" onClick={(e) => { e.stopPropagation(); onEmFechamento(c, true); }} data-e2e="comanda-imprimir"
                  title="Imprime o cupom de fechamento e põe a comanda em fechamento"
                  className="inline-flex items-center justify-center gap-1 text-[10px] min-[380px]:text-[11px] text-muted-foreground hover:text-foreground py-1.5 rounded-md hover:bg-muted/60 transition-colors touch-manipulation whitespace-nowrap">
                  <Printer className="w-3.5 h-3.5 shrink-0" /> Imprimir
                </button>
                <button type="button" onClick={(e) => { e.stopPropagation(); onEmFechamento(c, false); }} data-e2e="comanda-em-fechamento"
                  title="Põe a comanda em fechamento, sem imprimir cupom"
                  className="inline-flex items-center justify-center gap-1 text-[10px] min-[380px]:text-[11px] text-muted-foreground hover:text-foreground py-1.5 rounded-md hover:bg-muted/60 transition-colors touch-manipulation whitespace-nowrap">
                  <XCircle className="w-3.5 h-3.5 shrink-0" /> Fechar comanda
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
